<template>
  <el-dialog
      v-model="visible"
      title="EDI数据预览"
      width="80%"
      top="5vh"
      :before-close="handleClose"
  >
    <div class="edi-preview-container">
      <div class="edi-toolbar">
        <el-button size="small" @click="toggleHighlight">
          {{ highlightEnabled ? '关闭高亮' : '开启高亮' }}
        </el-button>
      </div>
      <div class="edi-content" ref="ediContent">
        <div v-if="loading" class="loading-container">
          <el-icon class="is-loading"><Loading /></el-icon>
          正在生成EDI数据...
        </div>
        <template v-else>
          <pre v-if="!highlightEnabled && safeDisplayEdiString">{{ safeDisplayEdiString }}</pre>
          <div v-else-if="highlightEnabled && safeHighlightedEdiData" v-html="safeHighlightedEdiData"></div>
          <div v-else class="no-data">暂无EDI数据</div>
        </template>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleClose">关闭</el-button>
        <el-button
            type="primary"
            @click="handleCopy"
            :disabled="!safeDisplayEdiString"
        >
          复制
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import {
  ElButton,
  ElMessage,
  ElDialog,
} from 'element-plus';
import { Loading } from '@element-plus/icons-vue';

export default {
  components: {
    ElButton,
    ElDialog,
    Loading
  },
  name: 'EdiPreview',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    ediString: {
      type: [String, Object],
      default: ''
    },
    ediParams: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      highlightEnabled: false,
      selectedSegment: '',
      segmentOptions: [],
      loading: false,
      internalEdiString: ''
    };
  },
  computed: {
    visible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },

    // 安全的EDI字符串获取
    safeDisplayEdiString() {
      try {
        const content = this.ediString || this.internalEdiString;
        if (typeof content === 'string') {
          return content;
        }
        if (content && typeof content.toString === 'function') {
          return content.toString();
        }
        return '';
      } catch (error) {
        console.error('获取EDI字符串失败:', error);
        return '';
      }
    },

    // 安全的高亮EDI数据 - 修正高亮规则
    // 安全的高亮EDI数据 - 修正高亮规则
    safeHighlightedEdiData() {
      try {
        const content = this.safeDisplayEdiString;
        if (!content) return '';

        // 先对内容进行HTML转义，防止XSS攻击
        let escapedContent = this.escapeHtml(content);

        if (this.highlightEnabled) {
          // 定义要高亮的字段模式 - 修正后的规则
          const highlightPatterns = [
            //STX
            {
              pattern: /(STX=ANA:1\+)(\d+)(:)/g,
              replacement: '$1<span class="highlight-red">$2</span>$3'
            },
            {
              pattern: /(SDT=)(\d+)(\+)/g,
              replacement: '$1<span class="highlight-red">$2</span>$3'
            },
            {
              pattern: /(CDT=)(\d+)(\+)/g,
              replacement: '$1<span class="highlight-red">$2</span>$3'
            },
            //PYT
            {
              pattern: /(PYT=1\+)([^&]*)(&#039;)/g,
              replacement: '$1<span class="highlight-blue">$2</span>$3'
            },
            //CLO
            {
              pattern: /(CLO=:)(\d+)(\+)/g,
              replacement: '$1<span class="highlight-blue">$2</span>$3'
            },
            //STX SDT
            {
              pattern: /(STX=ANA:1\+[^:]+:)(.+?)(\+[^:]+:)/g,
              replacement: '$1<span class="highlight-blue">$2</span>$3'
            },
            {
              pattern: /(SDT=[^+]+\+)([^+]+)(\+)/g,
              replacement: '$1<span class="highlight-blue">$2</span>$3'
            },
            // IRF行中的CIH编号高亮为蓝色
            {
              pattern: /(IRF=)([^+]+)(\+)/g,
              replacement: '$1<span class="highlight-blue">$2</span>$3'
            },
            // {
            //   pattern: /(IRF=[^+]+\+)(\d{6})(\+)/g,
            //   replacement: '$1<span class="highlight-blue">$2</span>$3'
            // },
            {
              pattern: /(IRF=[^+]+\+[^+]+\+)(\d{6})/g,
              replacement: '$1<span class="highlight-blue">$2</span>'
            },

            // ODD行中的时间段高亮为红色
            {
              pattern: /(ODD=1\+[^:]+:[^:]+:[^:]+:[^+]+\+)([^:]+)(:\d+)/g,
              replacement: '$1<span class="highlight-blue">$2</span>$3'
            },
            {
              pattern: /(ODD=1\+[^:]+:[^:]+:)(\d{6})(:[^+]+)/g,
              replacement: '$1<span class="highlight-blue">$2</span>$3'
            },
            {
              pattern: /(ODD=1\+[^:]+:[^:]+:[^:]+:[^+]+\+[^:]+:)(\d{6})/g,
              replacement: '$1<span class="highlight-blue">$2</span>'
            },
            // {
            //   pattern: /(ODD=1\+)([^:+]+)/g,
            //   replacement: '$1<span class="highlight-blue">$2</span>'
            // },

          ];

          // 应用所有高亮规则
          highlightPatterns.forEach(pattern => {
            escapedContent = escapedContent.replace(pattern.pattern, pattern.replacement);
          });

          // 额外处理：确保段名也有基本的高亮
          escapedContent = escapedContent.replace(
              /(STX|MHD|TYP|SDT|CDT|FIL|FDT|MTR|CLO|IRF|PYT|ODD|ILD|STL|TLR|VRS|TOT|END)=([^\n]*)/g,
              (match, segmentName = '', segmentContent = '') => {
                return `<div class="edi-segment"><span class="segment-name">${segmentName}</span>=<span class="segment-content">${segmentContent}</span></div>`;
              }
          );
        } else {
          // 不高亮时，只进行基本的段格式化
          escapedContent = escapedContent.replace(
              /(STX|MHD|TYP|SDT|CDT|FIL|FDT|MTR|CLO|IRF|PYT|ODD|ILD|STL|TLR|VRS|TOT|END)=([^\n]*)/g,
              (match, segmentName = '', segmentContent = '') => {
                return `<div class="edi-segment"><span class="segment-name">${segmentName}</span>=<span class="segment-content">${segmentContent}</span></div>`;
              }
          );
        }

        return escapedContent;
      } catch (error) {
        console.error('高亮EDI数据失败:', error);
        return this.escapeHtml(this.safeDisplayEdiString);
      }
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.initData();
        }
      }
    },
    safeDisplayEdiString: {
      immediate: true,
      handler(newVal) {
        this.updateSegmentOptions(newVal);
      }
    }
  },
  methods: {
    initData() {
      this.highlightEnabled = false;
      this.selectedSegment = '';
      this.segmentOptions = [];
    },

    handleClose() {
      this.visible = false;
    },

    handleCopy() {
      const textToCopy = this.safeDisplayEdiString;
      if (!textToCopy) {
        ElMessage.warning('没有可复制的内容');
        return;
      }

      navigator.clipboard.writeText(textToCopy)
          .then(() => {
            ElMessage.success('复制成功');
          })
          .catch((err) => {
            console.error('复制失败:', err);
            ElMessage.error('复制失败');
          });
    },

    toggleHighlight() {
      this.highlightEnabled = !this.highlightEnabled;
    },

    // HTML转义函数，防止XSS攻击
    escapeHtml(unsafe) {
      if (!unsafe) return '';
      return unsafe
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");
    },

    updateSegmentOptions(ediContent) {
      try {
        this.segmentOptions = [];

        if (!ediContent || typeof ediContent !== 'string') {
          return;
        }

        const segments = ediContent.split('\n');
        const options = [];
        const segmentCount = {};

        segments.forEach((segment, index) => {
          if (!segment || typeof segment !== 'string') return;

          const segmentMatch = segment.match(/^([A-Z]+)=/);
          if (segmentMatch?.[1]) {
            const segmentName = segmentMatch[1];
            segmentCount[segmentName] = (segmentCount[segmentName] || 0) + 1;

            options.push({
              value: `${segmentName}-${segmentCount[segmentName]}-${index}`,
              label: `${segmentName}段 (${segmentCount[segmentName]})`,
              index: index
            });
          }
        });

        this.segmentOptions = options;
      } catch (error) {
        console.error('更新EDI段选项失败:', error);
        this.segmentOptions = [];
      }
    }
  }
};
</script>

<style scoped>
.edi-preview-container {
  height: 70vh;
  display: flex;
  flex-direction: column;
}

.edi-toolbar {
  margin-bottom: 10px;
}

.edi-content {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 20px;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.5;
  overflow-y: auto;
  flex: 1;
  max-height: 60vh;
  position: relative;
  text-align: left;
  text-align-last: left;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  text-align: left;
}

.no-data {
  text-align: center;
  color: #999;
  padding: 20px;
}

.edi-content pre {
  margin: 0;
  color: #333;
  white-space: pre-wrap;
  word-wrap: break-word;
  text-align: left;
  text-align-last: left;
  display: block;
  unicode-bidi: embed;
  font-family: inherit;
}

.edi-segment {
  margin: 2px 0;
  line-height: 1.5;
  transition: background-color 0.3s ease;
  text-align: left;
  display: block;
}

.segment-name {
  font-weight: bold;
  color: #007acc;
  display: inline;
  text-align: left;
}

.segment-content {
  color: #333;
  display: inline;
  text-align: left;
}

.segment-highlight {
  background-color: #fff8e1;
  border-left: 3px solid #ffc107;
  padding-left: 10px;
}

.dialog-footer {
  margin-top: 20px;
  text-align: right;
}

/* 红色高亮样式 */
.highlight-red {
  color: #ff0000 !important;
  font-weight: bold;
  background-color: #fff0f0;
  padding: 1px 3px;
  border-radius: 2px;
  border: 1px solid #ffcccc;
}

/* 确保所有文本元素都左对齐 */
.edi-content * {
  text-align: left !important;
}

/* 针对高亮模式的特殊处理 */
.edi-content :deep(.edi-segment) {
  text-align: left !important;
  display: block;
}

.edi-content :deep(.segment-name) {
  text-align: left !important;
  display: inline;
}

.edi-content :deep(.segment-content) {
  text-align: left !important;
  display: inline;
}

/* 红色高亮样式的深度选择器 */
.edi-content :deep(.highlight-red) {
  color: #ff0000 !important;
  font-weight: bold;
  background-color: #fff0f0;
  padding: 1px 3px;
  border-radius: 2px;
  border: 1px solid #ffcccc;
}
/* 蓝色高亮样式 */
.highlight-blue {
  color: #0066cc !important;  /* 深蓝色 */
  font-weight: bold;
  background-color: #f0f8ff;  /* 浅蓝色背景 */
  padding: 1px 3px;
  border-radius: 2px;
  border: 1px solid #b3d9ff;
}

/* 蓝色高亮样式的深度选择器 */
.edi-content :deep(.highlight-blue) {
  color: #0066cc !important;
  font-weight: bold;
  background-color: #f0f8ff;
  padding: 1px 3px;
  border-radius: 2px;
  border: 1px solid #b3d9ff;
}
</style>